import { useState, useCallback } from 'react';
import { useCases } from '../../../core/di/useCases';
import { logger } from '../../../shared/utils/logger';
import { useNotification } from '../../../shared/hooks/useNotification';

/**
 * Hook pour gérer la suppression d'un post/activité
 * @returns {Object} Les fonctions et états pour supprimer une activité
 */
export const useDeletePost = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const notification = useNotification('posts');

  /**
   * Supprime un post/activité
   * @param {string} postId - L'ID du post à supprimer
   * @param {string} userId - L'ID de l'utilisateur qui fait la suppression
   * @param {boolean} showNotifications - Si true, affiche des notifications de succès/erreur
   * @returns {Promise<Object>} - Résultat de l'opération {success, error}
   */
  const deletePost = useCallback(async (postId, userId, showNotifications = false) => {
    if (!postId || !userId) {
      const message = 'Post ID and User ID are required';
      logger.error(message);
      setError(message);
      if (showNotifications) {
        notification.error('Impossible de supprimer l\'activité: informations manquantes');
      }
      return { success: false, error: message };
    }

    try {
      setLoading(true);
      setError(null);
      
      await useCases.deletePost.execute(postId, userId);
      
      if (showNotifications) {
        notification.success('Activité supprimée avec succès');
      }
      
      return { success: true };
    } catch (error) {
      logger.error('Error in deletePost:', error);
      setError(error.message);
      
      if (showNotifications) {
        notification.error('Erreur lors de la suppression de l\'activité');
      }
      
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [notification]);

  return {
    deletePost,
    loading,
    error
  };
}; 